import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { followUser, getAllergyPreferences, setAllergyPreferences } from '../utils/userService';
import { DEFAULT_VISIBILITY } from '../utils/conversions';
import { FOUNDER_UID } from '../config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  // Kept out of userProfile on purpose: these come from the owner-only
  // subcollection, and mixing them back into the profile object is how they
  // would drift into something that gets rendered on a public page.
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0); // bumps to re-read email_verified

  // Create the Firestore user doc on first sign-in (any provider). Returns
  // true when it creates a brand-new profile -- i.e. this is the user's first
  // sign-in ever -- so callers can route new users to their profile.
  async function ensureUserProfile(user, { displayName } = {}) {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const data = userSnap.data();
      // Self-heal: profiles used to snapshot the signup email into Firestore,
      // where the users read rule exposes it to any signed-in user -- and to
      // the whole web on isPublic accounts. Auth keeps the email server-side;
      // nothing reads this copy, so scrub it on sign-in. Fire-and-forget.
      if ('email' in data) {
        updateDoc(userRef, { email: deleteField() }).catch(() => {});
        delete data.email;
      }
      setUserProfile(data);
      return false;
    }

    const profile = {
      uid: user.uid,
      // Deliberately NO email field: the profile doc is readable by other
      // users (and anonymously when isPublic) -- see the scrub above.
      displayName: user.displayName || displayName || user.email?.split('@')[0] || 'Chef',
      photoURL: user.photoURL || null,
      role: 'consumer', // default role
      // New accounts start public so shared recipes preview properly and
      // profiles are reachable (disclosed at signup, changeable on
      // /profile). isPublic is the deprecated mirror the rules fall back to.
      visibility: DEFAULT_VISIBILITY,
      isPublic: DEFAULT_VISIBILITY === 'public',
      dietaryPreferences: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    await setDoc(userRef, profile);
    setUserProfile(profile);

    // Every new signup auto-follows the founder (unless they ARE the
    // founder) so Following/Featured aren't empty on day one -- a live
    // example of the follow feature. Non-blocking: don't fail account
    // creation if this write hiccups.
    if (user.uid !== FOUNDER_UID) {
      followUser(user.uid, FOUNDER_UID).catch(err =>
        console.error('Error auto-following founder:', err)
      );
    }

    return true;
  }

  // Sign-in helpers return whether this was the user's first sign-in, so the
  // login/signup pages can send new users to /profile to fill it out.
  async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return ensureUserProfile(result.user);
  }

  async function signUpWithEmail(name, email, password) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (name) {
      await updateProfile(result.user, { displayName: name });
    }
    const isNewUser = await ensureUserProfile(result.user, { displayName: name });
    // Non-blocking: users can browse unverified; the banner nudges them.
    sendEmailVerification(result.user).catch(err =>
      console.error('Error sending verification email:', err)
    );
    return isNewUser;
  }

  function resendVerificationEmail() {
    if (!auth.currentUser) return Promise.resolve();
    return sendEmailVerification(auth.currentUser);
  }

  // After a user clicks the verification link, refresh their auth state +
  // ID token so email_verified flips to true in-session (Firestore rules
  // read it from the token) without forcing a full sign-out/in.
  async function refreshUser() {
    if (!auth.currentUser) return;
    await auth.currentUser.reload();
    await auth.currentUser.getIdToken(true);
    setRefreshTick(t => t + 1);
  }

  async function signInWithEmail(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return ensureUserProfile(result.user);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function logout() {
    try {
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Server-side cascade: deletes profile/ratings/auth account and
  // anonymizes authored recipes. See the deleteAccount Cloud Function.
  async function deleteAccount() {
    const deleteFn = httpsCallable(functions, 'deleteAccount');
    await deleteFn();
    await signOut(auth).catch(() => {});
    setUserProfile(null);
  }

  // Admin-only (founder): delete ANY user's account, same cascade as
  // deleteAccount. Gated server-side in the adminDeleteUser Cloud Function --
  // this call fails harmlessly for anyone else.
  async function adminDeleteUser(targetUid) {
    const fn = httpsCallable(functions, 'adminDeleteUser');
    await fn({ targetUid });
  }

  // Admin-only: re-roll a recipe's AI photo, optionally with art direction.
  // Gated server-side in the regenerateRecipeImage Cloud Function.
  async function regenerateRecipeImage(recipeId, promptHint = '') {
    const fn = httpsCallable(functions, 'regenerateRecipeImage');
    await fn({ recipeId, promptHint });
  }

  // Founder-only: edit any recipe, and optionally move it to another author.
  // Gated server-side (assertFounder) -- and by the FOUNDER UID alone rather
  // than role:'admin', so promoting a moderator doesn't hand over the ability
  // to rewrite other people's work. Every edit is recorded on the document.
  async function adminUpdateRecipe(recipeId, updates, newAuthorId) {
    const fn = httpsCallable(functions, 'adminUpdateRecipe');
    const res = await fn({ recipeId, updates, newAuthorId });
    return res.data;
  }

  // Founder-only: edit any user's profile. Visibility, role, username, and
  // the user's own preferences are deliberately not editable -- see
  // ADMIN_PROFILE_FIELDS in functions/pure.js.
  async function adminUpdateProfile(targetUid, updates) {
    const fn = httpsCallable(functions, 'adminUpdateProfile');
    const res = await fn({ targetUid, updates });
    return res.data;
  }

  // Re-read the Firestore profile into context state. Needed after writes
  // that bypass updateUserProfile (e.g. claiming/clearing a custom URL),
  // so nav links depending on userProfile.username stay current this session.
  async function refreshUserProfile() {
    if (!currentUser) return;
    const snap = await getDoc(doc(db, 'users', currentUser.uid));
    if (snap.exists()) setUserProfile(snap.data());
  }

  async function updateUserProfile(data) {
    if (!currentUser) return;
    const userRef = doc(db, 'users', currentUser.uid);
    await setDoc(userRef, { ...data, updatedAt: serverTimestamp() }, { merge: true });
    setUserProfile(prev => ({ ...prev, ...data }));

    // Keep the Firebase Auth profile in sync so future recipes snapshot the
    // right author name/photo (recipes copy authorName/authorPhoto at create).
    const authUpdate = {};
    if (data.displayName !== undefined) authUpdate.displayName = data.displayName;
    if (data.photoURL !== undefined) authUpdate.photoURL = data.photoURL;
    if (Object.keys(authUpdate).length && auth.currentUser) {
      await updateProfile(auth.currentUser, authUpdate).catch(err =>
        console.error('Error syncing auth profile:', err)
      );
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const [userSnap, savedAllergies] = await Promise.all([
          getDoc(userRef),
          getAllergyPreferences(user.uid)
        ]);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserProfile(data);
          // Self-heal search indexing. searchTokens/searchable are written by
          // the onUserWritten trigger, which only fires on a write -- and
          // nothing writes user docs routinely, so accounts created before
          // search existed stayed unindexed and invisible to it. A harmless
          // touch fires the trigger; once indexed the condition is false and
          // this never runs again. Fire-and-forget: failing to index must
          // never block sign-in.
          if (!data.searchTokens) {
            updateDoc(userRef, { updatedAt: serverTimestamp() }).catch(() => {});
          }
        }
        setAllergies(savedAllergies);
      } else {
        setUserProfile(null);
        setAllergies([]);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Email/password accounts must verify before write actions; SSO
  // (Google) accounts arrive already verified. refreshTick forces this to
  // re-evaluate after refreshUser() reloads the account.
  // eslint-disable-next-line no-unused-vars
  const _tick = refreshTick;
  const needsEmailVerification =
    !!currentUser &&
    !auth.currentUser?.emailVerified &&
    (currentUser.providerData || []).some(p => p.providerId === 'password');

  // Allergy prefs are owner-only, so they're written straight to the private
  // subcollection rather than through updateUserProfile.
  async function updateAllergies(next) {
    if (!currentUser) return;
    await setAllergyPreferences(currentUser.uid, next);
    setAllergies(next);
  }

  const value = {
    currentUser,
    userProfile,
    allergies,
    updateAllergies,
    needsEmailVerification,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    resetPassword,
    resendVerificationEmail,
    refreshUser,
    deleteAccount,
    adminDeleteUser,
    regenerateRecipeImage,
    adminUpdateRecipe,
    adminUpdateProfile,
    logout,
    updateUserProfile,
    refreshUserProfile,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
